import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { formatDuration } from "@/lib/utils2";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { StickyNote, Clock, Trash2, Plus } from "lucide-react";

export default function EpisodeNotes({ episode, trigger }) {
  const { current, progress, seek, play } = usePlayer();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [withTime, setWithTime] = useState(true);
  const [busy, setBusy] = useState(false);

  const isCurrent = current && current.id === episode.id;

  const load = async () => {
    const { data } = await api.get(`/episodes/${episode.id}/notes`);
    setNotes(data.notes);
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const ts = withTime && isCurrent ? Math.floor(progress) : null;
      await api.post(`/episodes/${episode.id}/notes`, { text, timestamp_seconds: ts });
      setText("");
      toast.success("Note saved");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/notes/${id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const jump = (ts) => {
    if (ts == null) return;
    if (!isCurrent) play(episode);
    setTimeout(() => seek(ts), 250);
    toast.info(`Jumped to ${formatDuration(ts)}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg border-brand-line max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-brand" /> My notes
          </DialogTitle>
          <p className="text-sm text-brand-muted text-left truncate">{episode.title}</p>
        </DialogHeader>

        <div className="rounded-xl border border-brand-line bg-brand-sidebar/50 p-3">
          <Textarea
            data-testid="note-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Jot a shift lesson, a coaching point, a reminder…"
            rows={3}
            className="border-brand-line bg-white resize-none"
          />
          <div className="flex items-center justify-between mt-3">
            <label className="flex items-center gap-2 text-sm text-brand-muted">
              <Switch checked={withTime} onCheckedChange={setWithTime} data-testid="note-timestamp-toggle" />
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />
                Timestamp {isCurrent ? `(${formatDuration(progress)})` : "(play to capture)"}
              </span>
            </label>
            <Button size="sm" onClick={add} disabled={busy} className="rounded-full brand-btn" data-testid="add-note-button">
              <Plus className="w-4 h-4 mr-1.5" /> Add note
            </Button>
          </div>
        </div>

        <div className="mt-4 space-y-2 overflow-y-auto scrollbar-thin flex-1" data-testid="notes-list">
          {notes.length === 0 && <p className="text-sm text-brand-muted text-center py-6">No notes yet on this episode.</p>}
          {notes.map((n) => (
            <div key={n.id} className="group flex items-start gap-3 rounded-lg border border-brand-line bg-white p-3" data-testid={`note-${n.id}`}>
              {n.timestamp_seconds != null && (
                <button onClick={() => jump(n.timestamp_seconds)} className="shrink-0 mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-brand bg-brand/10 rounded-full px-2 py-0.5 hover:bg-brand/20 transition-colors" data-testid={`note-jump-${n.id}`}>
                  <Clock className="w-3 h-3" /> {formatDuration(n.timestamp_seconds)}
                </button>
              )}
              <p className="text-sm text-brand-ink flex-1 whitespace-pre-line">{n.text}</p>
              <button onClick={() => remove(n.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-brand-terracotta shrink-0" data-testid={`note-delete-${n.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
